import { useMemo } from 'react';
import { usePocketBase } from "../../PocketBaseContext";

export const useDashboardServices = () => {
    const pb = usePocketBase();


    return useMemo(() => ({
        getStatistics: async () => {
            try {
                const currentDate = new Date();
                const firstDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).toISOString();

                // Basit HTTP istekleri ile veri çekme
                const responses = await Promise.all([
                    fetch(`${pb.baseUrl}/api/collections/invoices/records`),
                    fetch(`${pb.baseUrl}/api/collections/products/records`),
                    fetch(`${pb.baseUrl}/api/collections/customers/records`)
                ]);

                const [invoicesRes, productsRes, customersRes] = await Promise.all(
                    responses.map(res => res.json())
                );

                // İstatistikleri hesapla
                const activeProducts = (productsRes?.items || []).filter(product => product.status === true).length;
                const pendingPayments = (invoicesRes?.items || []).filter(invoice => 
                    invoice.payment_status === 'Beklemede'
                ).length;

                return {
                    products: {
                        total: productsRes?.items?.length || 0,
                        active: (productsRes?.items || []).filter(product => product.status === true).length,
                        inactive: (productsRes?.items || []).filter(product => !product.status).length,
                        growth: 0
                    },
                    invoices: {
                        total: invoicesRes?.items?.length || 0,
                        completed: (invoicesRes?.items || []).filter(invoice => invoice.status === 'Onaylandı').length,
                        totalRevenue: (invoicesRes?.items || []).reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0),
                        monthlyRevenue: (invoicesRes?.items || [])
                            .filter(invoice => new Date(invoice.created) >= new Date(firstDayOfMonth))
                            .reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0),
                        growth: 0
                    },
                    customers: {
                        total: customersRes?.items?.length || 0,
                        kurumsal: (customersRes?.items || []).filter(customer => customer.kurumsallik === 'kurumsal').length,
                        bireysel: (customersRes?.items || []).filter(customer => customer.kurumsallik === 'bireysel').length,
                        growth: 0
                    }
                };
            } catch (error) {
                console.error("İstatistik verisi çekme hatası:", error);
                return {
                    projects: { total: 0, active: 0, completed: 0, growth: 0 },
                    invoices: { total: 0, pending: 0, totalRevenue: 0, monthlyRevenue: 0, growth: 0 },
                    customers: { total: 0, active: 0, growth: 0 }
                };
            }
        },

        getRecentActivities: async () => {
            try {
                // Son teklifleri getir (sürüm numarası ve tarih bilgisiyle)
                const result = await pb.collection('invoices').getList(1, 10, {
                    expand: 'customer'
                }); 
        
                // Aktiviteleri formatla
                const activities = result.items.map(invoice => {
                    const customer = invoice.expand?.customer;
                    const amount = invoice.total_amount || 0;
                    const currency = invoice.currency || 'TRY';
        
                    // Tarih formatlaması için güvenli dönüşüm
                    let formattedDate = '';
                    try {
                        formattedDate = new Date(invoice.created).toLocaleString('tr-TR', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                            hour: '2-digit',
                            minute: '2-digit'
                        });
                    } catch (e) {
                        formattedDate = invoice.created;
                    }
        
                    return {
                        id: invoice.id,
                        type: 'invoice_version',
                        title: invoice.title || 'İsimsiz Teklif',
                        description: `${customer?.name || 'Müşteri'} için ${amount} ${currency} tutarında teklif güncellendi`,
                        version: invoice.version,
                        version_note: invoice.version_note,
                        invoice_number: invoice.invoice_number,
                        created: formattedDate,
                        created_raw: invoice.created, // Ham tarih verisi
                        user: invoice.modified_by || 'Sistem',
                        icon: 'ri-file-list-3-line',
                        color: invoice.status === 'Taslak' ? 'warning' : 'success',
                        customerName: customer?.name,
                        amount: amount,
                        currency: currency,
                        status: invoice.status
                    };
                });
        
                return activities;
        
            } catch (error) {
                console.error("Aktivite verisi çekme hatası:", error);
                return [];
            }
        },
        
        getPocketBaseStatus: async () => {
            try {
                const response = await fetch(`${pb.baseUrl}/api/health`);
                const data = await response.json();
                
                return {
                    status: 'online',
                    lastChecked: new Date().toISOString(),
                    message: 'Bağlantı sağlandı',
                    baseUrl: pb.baseUrl,
                    totalStorageSize: 1024 * 1024 * 1024, // 1GB varsayılan
                    usedStorageSize: 0,
                    healthCheck: true
                };
            } catch (error) {
                console.error('PocketBase status error:', error);
                return {
                    status: 'offline',
                    lastChecked: new Date().toISOString(),
                    message: error.message || 'Bağlantı hatası',
                    healthCheck: false,
                    baseUrl: pb.baseUrl,
                    totalStorageSize: 0,
                    usedStorageSize: 0
                };
            }
        }
    }), [pb]);
};